import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "../../api/notificationService";

function formatDate(value) {
  if (!value) return "";
  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

export default function Notifications() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const loadNotifications = async (signal) => {
    setIsLoading(true);
    try {
      const payload = await fetchNotifications(signal);
      setNotifications(Array.isArray(payload?.notifications) ? payload.notifications : []);
      setUnreadCount(Number(payload?.unread_count || 0));
      setError("");
    } catch {
      setError("Chargement des notifications impossible.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    loadNotifications(controller.signal);
    return () => controller.abort();
  }, []);

  const markRead = async (notificationId) => {
    try {
      const payload = await markNotificationRead(notificationId);
      setNotifications((current) =>
        current.map((notification) =>
          notification.id === notificationId ? payload.notification : notification
        )
      );
      setUnreadCount((current) => Math.max(0, current - 1));
    } catch {
      setError("Impossible de marquer cette notification comme lue.");
    }
  };

  const markAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications((current) =>
        current.map((notification) => ({ ...notification, is_read: true }))
      );
      setUnreadCount(0);
    } catch {
      setError("Impossible de marquer les notifications comme lues.");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-5xl flex-col gap-4 px-4 py-10 sm:px-6 lg:px-8">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-sky-600">
              Espace joueur
            </p>
            <h1 className="mt-3 text-4xl font-black text-slate-950">
              Notifications
            </h1>
            <p className="mt-3 text-sm text-slate-600">
              {unreadCount > 0
                ? `${unreadCount} notification${unreadCount > 1 ? "s" : ""} non lue${unreadCount > 1 ? "s" : ""}.`
                : "Tout est lu pour le moment."}
            </p>
          </div>
          {unreadCount > 0 ? (
            <button
              type="button"
              onClick={markAllRead}
              className="self-start rounded-full bg-slate-950 px-5 py-3 text-sm font-bold text-white transition hover:bg-slate-800"
            >
              Tout marquer comme lu
            </button>
          ) : null}
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
        {error ? (
          <div className="mb-5 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        ) : null}

        {isLoading ? (
          <div className="rounded-2xl border border-slate-200 bg-white px-4 py-8 text-sm text-slate-500">
            Chargement des notifications...
          </div>
        ) : notifications.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-10 text-center text-sm text-slate-500">
            Aucune notification pour le moment.
          </div>
        ) : (
          <div className="space-y-3">
            {notifications.map((notification) => (
              <article
                key={notification.id}
                className={`rounded-2xl border p-4 shadow-sm ${
                  notification.is_read
                    ? "border-slate-200 bg-white"
                    : "border-sky-200 bg-sky-50"
                }`}
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-base font-black text-slate-950">
                        {notification.title}
                      </h2>
                      {!notification.is_read ? (
                        <span className="rounded-full bg-sky-600 px-2 py-0.5 text-xs font-bold text-white">
                          Nouveau
                        </span>
                      ) : null}
                    </div>
                    <p className="mt-2 text-sm leading-6 text-slate-600">
                      {notification.message}
                    </p>
                    <div className="mt-2 text-xs text-slate-400">
                      {formatDate(notification.created_at)}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {notification.link_url ? (
                      <Link
                        to={notification.link_url}
                        className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-100"
                      >
                        Ouvrir
                      </Link>
                    ) : null}
                    {!notification.is_read ? (
                      <button
                        type="button"
                        onClick={() => markRead(notification.id)}
                        className="rounded-full bg-slate-950 px-4 py-2 text-sm font-bold text-white transition hover:bg-slate-800"
                      >
                        Marquer comme lu
                      </button>
                    ) : null}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
